import { useEffect, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronRight,
  Copy,
  Download,
  ExternalLink,
  FileCheck2,
  FileText,
  Image,
  Import,
  KeyRound,
  LayoutTemplate,
  LibraryBig,
  Link2,
  ListChecks,
  LockKeyhole,
  Megaphone,
  Plus,
  SearchCheck,
  Sparkles,
  Trash2,
} from "lucide-react";
import {
  aPlusStoryboards,
  CUSTOM_COVER_PALETTE_ID,
  coverFamilySystems,
  coverPalettes,
  crossSellTemplates,
  interiorLayoutMasters,
  leadMagnetSystems,
  resolveCoverPalette,
  welcomeSequences,
} from "../data/expansion";
import { interiorModules } from "../data/catalog";
import {
  getChannelAccountReady,
  getChannelListing,
  getMarketplaceGuide,
  getPrimaryFileLabel,
  getPreflightChecks,
  getSellingKitGuide,
  recommendedLayoutIds,
  recommendedModuleIds,
} from "../data/marketplaces";
import {
  composeCoverPdfCreationPrompt,
  composeCustomerFileCreationPrompt,
  composeInteriorPdfCreationPrompt,
  composeSellingKitPrompt,
} from "../data/prompts";
import {
  ListingRecordSchema,
  type APlusModule,
  type ListingRecord,
  type OfferSku,
  type PaperKingdomProject,
  type ProductionCoverPlan,
  type ProductionInteriorPlan,
  type ProductionState,
} from "../domain/schema";
import { copyText, downloadGrowthPackage, downloadProductionDossier } from "../lib/downloads";
import {
  buildAdaptivePageBlueprint,
  getCoverFileBlueprintSignature,
  getPrimaryFileBlueprintSignature,
} from "../lib/pageBlueprint";
import { normalizePagePlanText } from "../lib/promptResults";
import { parseSellingKitAnswer } from "../lib/sellingKit";
import { getOfferPurchaseLink } from "../data/offerLinks";
import {
  AppSelect,
  Badge,
  Button,
  EmptyState,
  Field,
  OfferBadge,
  SectionHeading,
  TextArea,
  TextInput,
} from "../components/ui";

type SetProject = (updater: (project: PaperKingdomProject) => PaperKingdomProject) => void;

interface Stage {
  id: string;
  label: string;
  access?: string;
  disabled?: boolean;
  icon: React.ComponentType<{ size?: number }>;
}

function returnToPanelTop() {
  window.requestAnimationFrame(() => {
    const target = document.querySelector(".workflow-navigator") ?? document.querySelector(".studio-panel");
    target?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "start",
    });
  });
}

function ExpansionNavigator({
  stages,
  activeStage,
  onChange,
}: {
  stages: Stage[];
  activeStage: string;
  onChange: (stage: string) => void;
}) {
  const activeIndex = Math.max(0, stages.findIndex((stage) => stage.id === activeStage));
  return (
    <div className="workflow-navigator">
      <div
        className="workflow-progress"
        aria-hidden="true"
        style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))` }}
      >
        {stages.map((stage, index) => (
          <div className={index <= activeIndex ? "is-complete" : ""} key={stage.id}>
            <span>{index + 1}</span>
            <small>Step {index + 1}</small>
          </div>
        ))}
      </div>
      <div
        className="workflow-tabs"
        role="tablist"
        aria-label="Tasks in this step"
        style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))` }}
      >
        {stages.map((stage, index) => {
          const Icon = stage.icon;
          const active = stage.id === activeStage;
          return (
            <button
              key={stage.id}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={stage.disabled}
              className={active ? "is-active" : ""}
              onClick={() => {
                onChange(stage.id);
                returnToPanelTop();
              }}
            >
              <span className="workflow-tabs__icon"><Icon size={18} /></span>
              <span>
                <strong>{stage.label}</strong>
              </span>
              {stage.access ? <Badge tone={active ? "green" : "clay"}>{stage.access}</Badge> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WorkflowFinish({
  label,
  onClick,
  disabled = false,
  help,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  help?: string;
}) {
  return (
    <div className="workflow-continue">
      <Button
        size="sm"
        disabled={disabled}
        onClick={() => {
          onClick();
          returnToPanelTop();
        }}
      >
        Next: {label} <ChevronRight size={15} />
      </Button>
      {help ? <p className="form-help">{help}</p> : null}
    </div>
  );
}

function LockedExpansion({
  title,
  description,
  offer,
}: {
  title: string;
  description: string;
  offer: OfferSku;
}) {
  const purchase = getOfferPurchaseLink(offer);
  return (
    <div className="studio-panel expansion-locked">
      <EmptyState
        icon={<LockKeyhole size={24} />}
        title={title}
        description={description}
      >
        <div className="locked-expansion__actions">
          <OfferBadge sku={offer} locked />
          {purchase ? (
            <a
              className="button button--primary"
              href={purchase.url}
              target="_blank"
              rel="noreferrer"
            >
              {purchase.label} <ExternalLink size={15} />
            </a>
          ) : null}
        </div>
      </EmptyState>
    </div>
  );
}

function todayInputValue() {
  const now = new Date();
  const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return localDate.toISOString().slice(0, 10);
}

function sentenceFragment(value: string): string {
  const clean = value.trim().replace(/[.!?]+$/, "");
  if (!clean) return "";
  return `${clean.charAt(0).toLowerCase()}${clean.slice(1)}`;
}

function joinPlainList(values: string[]): string {
  if (values.length <= 1) return values[0] ?? "";
  if (values.length === 2) return values.join(" and ");
  return `${values.slice(0, -1).join(", ")}, and ${values.at(-1)}`;
}

function createStarterBackCoverCopy(
  project: PaperKingdomProject,
  book: PaperKingdomProject["catalogue"][number],
): string {
  const audience = sentenceFragment(project.audience) || "the intended reader";
  const purpose =
    sentenceFragment(book.differentiator || project.theme) ||
    "keep useful information together";
  const interior = project.production.interiorPlans[book.id];
  const pageNames = [
    ...(interior?.layoutIds ?? []).map(
      (id) => interiorLayoutMasters.find((layout) => layout.id === id)?.name,
    ),
    ...(interior?.moduleIds ?? []).map(
      (id) => interiorModules.find((module) => module.id === id)?.name,
    ),
  ].filter((name): name is string => Boolean(name));
  const uniquePages = [...new Set(pageNames)].slice(0, 5);
  const inside = uniquePages.length
    ? `Inside, the reader will find ${joinPlainList(uniquePages)}.`
    : "Inside, clear guided pages make each step easy to follow.";

  return `${book.title || "This product"} is a simple ${project.format.toLowerCase()} for ${audience}. It helps them ${purpose}. ${inside} Use it to keep useful details together and prepare for the next step.`;
}

function pageUseLabel(id: string, name: string): string {
  const value = `${id} ${name}`.toLowerCase();
  if (/orientation|section opener|closing|completion|next-volume/.test(value)) {
    return "Appears once";
  }
  if (/daily|weekly|journal|entry|observation record|check-in/.test(value)) {
    return "Repeats";
  }
  return "Use when needed";
}

const coverStyleHelp: Record<
  string,
  { bestFor: string; layout: string; image: string; lettering: string; colours: string }
> = {
  "modern-botanical": {
    bestFor: "A calm book with one simple nature-inspired image.",
    layout: "Clear title area with one main image below it.",
    image: "Use one original leaf, plant, or organic shape.",
    lettering: "A traditional title with simple supporting text.",
    colours: "Dark green, warm paper, and one natural accent.",
  },
  "editorial-minimal": {
    bestFor: "A modern, professional book that relies on a strong title.",
    layout: "Large title, clean alignment, and very little decoration.",
    image: "Use one small supporting mark only if it helps.",
    lettering: "Strong title lettering with simple small text.",
    colours: "Warm white, dark ink, and one accent colour.",
  },
  "heritage-library": {
    bestFor: "A traditional, collected, or keepsake feeling.",
    layout: "A framed title area similar to an old library label.",
    image: "Use an original or clearly licensed line ornament.",
    lettering: "Classic book lettering with generous spacing.",
    colours: "Deep red, cream, brass, and one muted accent.",
  },
  "playful-cut-paper": {
    bestFor: "A friendly activity book or imaginative family product.",
    layout: "Bold paper shapes around a large readable title area.",
    image: "Build one simple original scene from cut-paper shapes.",
    lettering: "Friendly, rounded, and easy to read.",
    colours: "Bright blue, coral, yellow, mint, and a neutral base.",
  },
  "soft-geometric": {
    bestFor: "A reassuring planner or workbook with an orderly feel.",
    layout: "Rounded shapes repeat around a stable title panel.",
    image: "Use simple arches, tiles, blocks, or paths.",
    lettering: "Warm modern lettering with a small classic accent.",
    colours: "Sage, sand, charcoal, and one quiet accent.",
  },
  "field-notes": {
    bestFor: "A practical guide based on tools, objects, or observations.",
    layout: "One main object with simple notes around it.",
    image: "Use an original drawing or licensed photo of the subject.",
    lettering: "Clear practical lettering that feels like a field guide.",
    colours: "Olive, brown paper, dark grey, and one natural accent.",
  },
  "quiet-luxury": {
    bestFor: "A refined gift book with plenty of open space.",
    layout: "Small central title with generous empty space.",
    image: "Use texture or one small emblem instead of busy decoration.",
    lettering: "Elegant and deliberately simple.",
    colours: "Dark brown, ivory, muted gold, and one mineral accent.",
  },
  "bold-type": {
    bestFor: "A direct book that must be easy to read at a small size.",
    layout: "A very large title with one small repeatable mark.",
    image: "Use little or no extra imagery.",
    lettering: "Heavy, clear title lettering.",
    colours: "Black, warm white, and one strong accent.",
  },
  "science-grid": {
    bestFor: "A science, research, health-record, or technical product that should feel exact.",
    layout: "A clear information grid with one measured title area.",
    image: "Use an original diagram, mapped object, or simple data pattern.",
    lettering: "Highly legible practical lettering with precise small labels.",
    colours: "Choose the separate Scientific & precise palette, or another product palette that fits.",
  },
};

export function ProductionPanel({
  project,
  entitlements,
  setProject,
  onComplete,
}: {
  project: PaperKingdomProject;
  entitlements: OfferSku[];
  setProject: SetProject;
  onComplete: () => void;
}) {
  const [activeStage, setActiveStage] = useState("cover");
  const [activeBookId, setActiveBookId] = useState(project.catalogue[0]?.id ?? "");
  const [layoutToAdd, setLayoutToAdd] = useState("");
  const [moduleToAdd, setModuleToAdd] = useState("");
  const [rightsDraft, setRightsDraft] = useState({
    asset: "",
    source: "",
    licence: "",
    status: "to-check" as "to-check" | "cleared" | "restricted",
    notes: "",
  });
  const [proofDraft, setProofDraft] = useState({
    bookId: project.catalogue[0]?.id ?? "",
    reviewedAt: todayInputValue(),
    outcome: "pending" as "pending" | "changes-needed" | "approved",
    notes: "",
  });
  const [downloaded, setDownloaded] = useState(false);
  const [filePromptCopyState, setFilePromptCopyState] = useState<
    "idle" | "primary" | "cover" | "error"
  >("idle");
  const hasProduction = entitlements.includes("paper-kingdom-production");

  useEffect(() => {
    if (!hasProduction) return;
    const book =
      project.catalogue.find((item) => item.id === activeBookId) ??
      project.catalogue[0];
    if (!book || project.production.coverPlans[book.id]?.backCoverCopy.trim()) {
      return;
    }
    const draft = createStarterBackCoverCopy(project, book);
    setProject((current) => {
      const currentBook =
        current.catalogue.find((item) => item.id === book.id) ??
        current.catalogue[0];
      if (!currentBook) return current;
      const existing = current.production.coverPlans[currentBook.id];
      if (existing?.backCoverCopy.trim()) return current;
      const system = coverFamilySystems.find(
        (item) => item.id === current.production.coverSystemId,
      );
      return {
        ...current,
        production: {
          ...current.production,
          coverPlans: {
            ...current.production.coverPlans,
            [currentBook.id]: {
              variant:
                existing?.variant ||
                (system
                  ? system.variants[
                      (currentBook.sequence - 1) % system.variants.length
                    ]
                  : ""),
              titleTreatment: existing?.titleTreatment || "",
              subtitleTreatment: existing?.subtitleTreatment || "",
              focalBrief: existing?.focalBrief || "",
              backCoverCopy: draft,
              differentiationNote: existing?.differentiationNote || "",
            },
          },
        },
      };
    });
  }, [
    activeBookId,
    hasProduction,
    project,
    setProject,
  ]);

  useEffect(() => {
    if (!hasProduction) return;
    const needsUpdate = Object.values(project.production.interiorPlans).some(
      (plan) => normalizePagePlanText(plan.sequenceNotes) !== plan.sequenceNotes,
    );
    if (!needsUpdate) return;
    setProject((current) => ({
      ...current,
      production: {
        ...current.production,
        interiorPlans: Object.fromEntries(
          Object.entries(current.production.interiorPlans).map(([bookId, plan]) => [
            bookId,
            {
              ...plan,
              sequenceNotes: normalizePagePlanText(plan.sequenceNotes),
            },
          ]),
        ),
      },
    }));
  }, [hasProduction, project.production.interiorPlans, setProject]);

  if (!hasProduction) {
    return (
      <LockedExpansion
        offer="paper-kingdom-production"
        title="Book-making tools are not enabled"
        description="Unlock reusable cover styles, inside-page planning, and prompts for creating the product files."
      />
    );
  }

  const marketplaceGuide = getMarketplaceGuide(project.marketplace);
  const isBook = marketplaceGuide.usesKdpPrintSetup;
  const itemNoun = isBook ? "book" : "product";
  const visualNoun = isBook ? "cover" : "listing preview";
  const preflightChecks = getPreflightChecks(project);
  const suggestedLayoutIds = recommendedLayoutIds(project.format);
  const suggestedModuleIds = recommendedModuleIds(project.format);
  const updateProduction = (patch: Partial<ProductionState>) =>
    setProject((current) => ({
      ...current,
      production: { ...current.production, ...patch },
    }));
  const updateCustomPaletteColor = (index: number, hex: string) =>
    setProject((current) => ({
      ...current,
      production: {
        ...current.production,
        coverPaletteId: CUSTOM_COVER_PALETTE_ID,
        customCoverPalette: {
          ...current.production.customCoverPalette,
          colors: current.production.customCoverPalette.colors.map((color, colorIndex) =>
            colorIndex === index ? { ...color, hex } : color,
          ),
        },
      },
    }));
  const chooseCoverSystem = (systemId: string) =>
    setProject((current) => {
      const nextSystem = coverFamilySystems.find(
        (system) => system.id === systemId,
      );
      const coverPlans = { ...current.production.coverPlans };
      if (nextSystem) {
        current.catalogue.forEach((book, index) => {
          const existing = coverPlans[book.id];
          if (!existing) return;
          coverPlans[book.id] = {
            ...existing,
            variant: nextSystem.variants[index % nextSystem.variants.length],
          };
        });
      }
      return {
        ...current,
        production: {
          ...current.production,
          coverSystemId: systemId,
          coverPaletteId:
            current.production.coverPaletteId ||
            {
              "modern-botanical": "natural-calm",
              "editorial-minimal": "bold-monochrome",
              "heritage-library": "natural-calm",
              "playful-cut-paper": "playful-bright",
              "soft-geometric": "soft-expressive",
              "field-notes": "strong-grounded",
              "quiet-luxury": "soft-expressive",
              "bold-type": "bold-monochrome",
              "science-grid": "scientific-precise",
            }[systemId] ||
            "",
          coverPlans,
        },
      };
    });
  const selectedSystem = coverFamilySystems.find(
    (system) => system.id === project.production.coverSystemId,
  );
  const selectedPalette = resolveCoverPalette(
    project.production.coverPaletteId,
    project.production.customCoverPalette,
  );
  const activeSystem = selectedSystem ?? coverFamilySystems[0];
  const activeStyleHelp = selectedSystem
    ? coverStyleHelp[selectedSystem.id] ?? {
        bestFor: selectedSystem.character,
        layout: selectedSystem.grid,
        image: selectedSystem.focalRule,
        lettering: selectedSystem.typeRule,
        colours: selectedSystem.paletteRule,
      }
    : {
        bestFor: `Choose the closest style for this specific ${itemNoun}.`,
        layout: "Shown after you choose a style.",
        image: "Shown after you choose a style.",
        lettering: "Shown after you choose a style.",
        colours: "Shown after you choose a style.",
      };
  const activeBook = project.catalogue.find((book) => book.id === activeBookId) ?? project.catalogue[0];
  const activeCoverPlan: ProductionCoverPlan =
    (activeBook && project.production.coverPlans[activeBook.id]) ?? {
      variant: selectedSystem
        ? selectedSystem.variants[
            activeBook?.sequence ? (activeBook.sequence - 1) % 6 : 0
          ]
        : "",
      titleTreatment: "",
      subtitleTreatment: "",
      focalBrief: "",
      backCoverCopy: "",
      differentiationNote: "",
    };
  const activeInteriorPlan: ProductionInteriorPlan =
    (activeBook && project.production.interiorPlans[activeBook.id]) ?? {
      layoutIds: [],
      moduleIds: [],
      sequenceNotes: "",
    };
  const primaryFileLabel = getPrimaryFileLabel(project);
  const primaryCreationPrompt = activeBook
    ? isBook
      ? composeInteriorPdfCreationPrompt(project, activeBook.id)
      : composeCustomerFileCreationPrompt(project, activeBook.id)
    : "";
  const adaptivePageBlueprint = activeBook
    ? buildAdaptivePageBlueprint(project, activeBook.id)
    : undefined;
  const currentPrimaryFileSignature = activeBook
    ? getPrimaryFileBlueprintSignature(project, activeBook.id)
    : "";
  const currentCoverFileSignature = activeBook
    ? getCoverFileBlueprintSignature(project, activeBook.id)
    : "";
  const coverCreationPrompt = activeBook
    ? composeCoverPdfCreationPrompt(project, activeBook.id)
    : "";
  useEffect(() => {
    setFilePromptCopyState("idle");
  }, [primaryCreationPrompt, coverCreationPrompt]);
  const selectedPagePlan = [
    ...activeInteriorPlan.layoutIds.flatMap((id) => {
      const layout = interiorLayoutMasters.find((item) => item.id === id);
      return layout
        ? [{
            key: `layout:${id}`,
            id,
            source: "layout" as const,
            name: layout.name,
            purpose: layout.purpose,
          }]
        : [];
    }),
    ...activeInteriorPlan.moduleIds.flatMap((id) => {
      const module = interiorModules.find((item) => item.id === id);
      return module
        ? [{
            key: `module:${id}`,
            id,
            source: "module" as const,
            name: module.name,
            purpose: module.purpose,
          }]
        : [];
    }),
  ];
  const emptyCustomerFileChecks: ProductionState["customerFileChecks"] = {
    interiorPdfOpens: false,
    interiorSizeMatches: false,
    pageOrderChecked: false,
    fontsEmbedded: false,
    coverPdfOpens: false,
    coverDimensionsMatch: false,
    barcodeAreaClear: false,
  };
  const coverReady = Boolean(
    activeBook?.title.trim() &&
      project.production.coverSystemId &&
      project.production.coverPaletteId &&
      activeCoverPlan.backCoverCopy.trim(),
  );
  const interiorReady = Boolean(
    coverReady && selectedPagePlan.length && activeInteriorPlan.sequenceNotes.trim(),
  );
  const interiorFileReady = Boolean(
    project.production.primaryFileCreated &&
      project.production.primaryFilePromptSignature === currentPrimaryFileSignature,
  );
  const coverFileReady = Boolean(
    interiorFileReady &&
      project.production.coverPdfCreated &&
      project.production.coverFilePromptSignature === currentCoverFileSignature,
  );
  const customerFilesReady = isBook
    ? coverFileReady
    : interiorFileReady;
  const productionStages: Stage[] = [
    { id: "cover", label: isBook ? "Cover" : "Listing preview", icon: Image },
    {
      id: "interior",
      label: isBook ? "Inside pages" : "Product pages",
      icon: LayoutTemplate,
      disabled: !coverReady,
    },
    {
      id: "specs",
      label: "Create files",
      icon: FileText,
      disabled: !interiorReady,
    },
  ];
  const availableModules = interiorModules
    .filter((module) => entitlements.includes(module.requiredOffer))
    .sort((first, second) => {
      const firstRecommended = suggestedModuleIds.includes(first.id) ? 0 : 1;
      const secondRecommended = suggestedModuleIds.includes(second.id) ? 0 : 1;
      return firstRecommended - secondRecommended;
    });
  const suggestedLayouts = interiorLayoutMasters.filter((layout) =>
    suggestedLayoutIds.includes(layout.id),
  );

  const updateCoverPlan = (patch: Partial<ProductionCoverPlan>) => {
    if (!activeBook) return;
    updateProduction({
      coverPlans: {
        ...project.production.coverPlans,
        [activeBook.id]: { ...activeCoverPlan, ...patch },
      },
    });
  };

  const updateInteriorPlan = (patch: Partial<ProductionInteriorPlan>) => {
    if (!activeBook) return;
    updateProduction({
      interiorPlans: {
        ...project.production.interiorPlans,
        [activeBook.id]: { ...activeInteriorPlan, ...patch },
      },
    });
  };

  const updateFileSetup = (
    patch: Partial<Pick<
      ProductionState,
      | "trimSize"
      | "paperType"
      | "bleed"
      | "typographySpecification"
    >>,
  ) =>
    setProject((current) => ({
      ...current,
      production: {
        ...current.production,
        ...patch,
        primaryFileCreated: false,
        primaryFilePromptSignature: "",
        interiorPdfFileName: "",
        interiorPageCount: 0,
        confirmedSpineWidth: "",
        coverDimensions: "",
        coverPdfCreated: false,
        coverFilePromptSignature: "",
        coverPdfFileName: "",
        customerFileChecks: emptyCustomerFileChecks,
        exportSettings: "",
      },
    }));

  const togglePrimaryFileCreated = () =>
    setProject((current) => ({
      ...current,
      production: {
        ...current.production,
        primaryFileCreated: !interiorFileReady,
        primaryFilePromptSignature: interiorFileReady ? "" : currentPrimaryFileSignature,
        confirmedSpineWidth: "",
        coverDimensions: "",
        coverPdfCreated: false,
        coverFilePromptSignature: "",
        coverPdfFileName: "",
        customerFileChecks: emptyCustomerFileChecks,
        exportSettings: "",
      },
    }));

  const toggleCoverFileCreated = () =>
    setProject((current) => ({
      ...current,
      production: {
        ...current.production,
        coverPdfCreated: !coverFileReady,
        coverFilePromptSignature: coverFileReady ? "" : currentCoverFileSignature,
        customerFileChecks: emptyCustomerFileChecks,
        exportSettings: "",
      },
    }));

  const copyFileCreationPrompt = async (
    kind: "primary" | "cover",
    prompt: string,
  ) => {
    try {
      await copyText(prompt);
      setFilePromptCopyState(kind);
    } catch {
      setFilePromptCopyState("error");
    }
  };

  const addRightsItem = () => {
    if (!rightsDraft.asset.trim()) return;
    updateProduction({
      rightsLedger: [
        ...project.production.rightsLedger,
        { id: crypto.randomUUID(), ...rightsDraft, asset: rightsDraft.asset.trim() },
      ],
    });
    setRightsDraft({ asset: "", source: "", licence: "", status: "to-check", notes: "" });
  };

  const addProof = () => {
    if (
      !proofDraft.bookId ||
      !proofDraft.reviewedAt ||
      proofDraft.outcome === "pending" ||
      !proofDraft.notes.trim()
    ) {
      return;
    }
    updateProduction({
      proofLog: [
        ...project.production.proofLog,
        { id: crypto.randomUUID(), ...proofDraft },
      ],
    });
    setProofDraft({
      bookId: project.catalogue[0]?.id ?? "",
      reviewedAt: todayInputValue(),
      outcome: "pending",
      notes: "",
    });
  };
  const proofMissing = [
    !proofDraft.bookId ? `choose a ${itemNoun}` : "",
    !proofDraft.reviewedAt ? "add the review date" : "",
    proofDraft.outcome === "pending" ? "choose the result" : "",
    !proofDraft.notes.trim() ? "add short review notes" : "",
  ].filter(Boolean);

  return (
    <div className="studio-panel expansion-panel">
      <ExpansionNavigator
        stages={productionStages}
        activeStage={activeStage}
        onChange={setActiveStage}
      />

      {activeStage === "cover" ? (
        <section className="workflow-stage" role="tabpanel">
          <SectionHeading
            eyebrow={isBook ? "Cover" : "Listing preview"}
            title={isBook ? "Cover" : "Listing preview"}
          />
          <div className="form-card">
            <Field label={`${isBook ? "Cover" : "Preview"} style`}>
              <AppSelect
                value={project.production.coverSystemId}
                aria-label={`${isBook ? "Cover" : "Preview"} style`}
                onChange={(event) => chooseCoverSystem(event.target.value)}
              >
                <option value="">Choose a visual style</option>
                {coverFamilySystems.map((system) => (
                  <option value={system.id} key={system.id}>{system.name}</option>
                ))}
              </AppSelect>
            </Field>
            <p className="field-explanation">{activeStyleHelp.bestFor}</p>
          </div>
          <div className="form-card">
            <div className="palette-heading">
              <div>
                <span className="eyebrow">Product colours</span>
                <h3>Colours</h3>
              </div>
              {selectedPalette ? <Badge tone="green">Selected</Badge> : null}
            </div>
            <div className="palette-choice-grid" role="radiogroup" aria-label="Product colour palette">
              {coverPalettes.map((palette) => (
                <button
                  type="button"
                  role="radio"
                  aria-checked={project.production.coverPaletteId === palette.id}
                  className={`palette-choice ${
                    project.production.coverPaletteId === palette.id ? "is-selected" : ""
                  }`}
                  key={palette.id}
                  onClick={() => updateProduction({ coverPaletteId: palette.id })}
                >
                  <span className="palette-choice__swatches" aria-hidden="true">
                    {palette.colors.map((color) => (
                      <i key={color.hex} style={{ backgroundColor: color.hex }} />
                    ))}
                  </span>
                  <strong>{palette.name}</strong>
                  <small>{palette.character}</small>
                  <span>{palette.bestFor}</span>
                </button>
              ))}
              <button
                type="button"
                role="radio"
                aria-checked={project.production.coverPaletteId === CUSTOM_COVER_PALETTE_ID}
                className={`palette-choice ${
                  project.production.coverPaletteId === CUSTOM_COVER_PALETTE_ID ? "is-selected" : ""
                }`}
                onClick={() => updateProduction({ coverPaletteId: CUSTOM_COVER_PALETTE_ID })}
              >
                <span className="palette-choice__swatches" aria-hidden="true">
                  {project.production.customCoverPalette.colors.map((color) => (
                    <i key={color.name} style={{ backgroundColor: color.hex }} />
                  ))}
                </span>
                <strong>Choose my own colours</strong>
                <small>Pick the colours yourself</small>
                <span>Use this when none of the ready-made choices feels right.</span>
              </button>
            </div>
            {project.production.coverPaletteId === CUSTOM_COVER_PALETTE_ID ? (
              <div className="custom-palette-editor">
                <div>
                  <strong>Pick four colours</strong>
                  <p>Click each colour square and choose the colour you want.</p>
                </div>
                <div className="custom-palette-editor__fields">
                  {project.production.customCoverPalette.colors.map((color, index) => (
                    <label className="color-field" key={color.name}>
                      <input
                        type="color"
                        value={color.hex}
                        aria-label={color.name}
                        onChange={(event) => updateCustomPaletteColor(index, event.target.value)}
                      />
                      <span className="color-field__copy">
                        <strong>{color.name}</strong>
                        <code>{color.hex.toUpperCase()}</code>
                      </span>
                    </label>
                  ))}
                </div>
                <p className="custom-palette-editor__note">
                  These colours will be included in the cover prompt and your downloaded production plan.
                </p>
              </div>
            ) : null}
            {selectedPalette ? (
              <p className="field-explanation">
                {selectedPalette.colors
                  .map((color) => `${color.name} (${color.hex})`)
                  .join(" · ")}
              </p>
            ) : null}
          </div>

          {project.catalogue.length > 1 ? (
            <div className="expansion-detail-card">
              <details className="beginner-advanced">
                <summary>
                  <span>
                    <strong>See the collection {visualNoun} variations</strong>
                    <small>Optional. Use these only when more than one {itemNoun} is being developed.</small>
                  </span>
                  <ChevronRight size={17} />
                </summary>
                <div className="cover-variant-grid">
                  {activeSystem.variants.slice(0, project.catalogue.length).map((variant, index) => {
                    const book = project.catalogue[index];
                    return (
                      <article key={variant}>
                        <span>{String(index + 1).padStart(2, "0")}</span>
                        <strong>{variant}</strong>
                        <small>{book?.title || `${itemNoun[0].toUpperCase()}${itemNoun.slice(1)} ${index + 1}`}</small>
                      </article>
                    );
                  })}
                </div>
              </details>
            </div>
          ) : null}

          <div className="form-card expansion-form-grid">
            {project.catalogue.length > 1 ? (
              <div className="cover-book-switcher">
                <Field label={`Which ${itemNoun} are you finishing?`}>
                  <AppSelect
                    value={activeBook?.id ?? ""}
                    onChange={(event) => setActiveBookId(event.target.value)}
                    aria-label={`${visualNoun} plan title`}
                  >
                    {project.catalogue.map((book) => (
                      <option key={book.id} value={book.id}>
                        {book.sequence}. {book.title || `Untitled ${itemNoun}`}
                      </option>
                    ))}
                  </AppSelect>
                </Field>
              </div>
            ) : null}

            <div className="cover-copy-handoff">
              <div>
                <span className="eyebrow">
                  {isBook ? "Front-cover text" : "Preview text"}
                </span>
                <h3>{isBook ? "Front-cover text" : "Preview text"}</h3>
              </div>
              <dl>
                <div>
                  <dt>Title</dt>
                  <dd>{activeBook?.title || `Untitled ${itemNoun}`}</dd>
                </div>
                {activeBook?.subtitle.trim() ? (
                  <div>
                    <dt>Subtitle</dt>
                    <dd>{activeBook.subtitle}</dd>
                  </div>
                ) : null}
                {project.imprint.name.trim() &&
                project.imprint.name !== "My Publishing Name" ? (
                  <div>
                    <dt>{isBook ? "Publishing name" : "Brand name"}</dt>
                    <dd>{project.imprint.name}</dd>
                  </div>
                ) : null}
              </dl>
            </div>

            <Field label={isBook ? "Back-cover text" : "Preview text"}>
              <TextArea
                rows={4}
                maxLength={2400}
                placeholder="Example: A simple guided book for recording useful details, preparing questions, and keeping everything in one place."
                value={activeCoverPlan.backCoverCopy}
                onChange={(event) => updateCoverPlan({ backCoverCopy: event.target.value })}
              />
            </Field>
            <details className="beginner-advanced">
              <summary>
                <span>
                  <strong>One extra cover instruction (optional)</strong>
                  <small>Optional</small>
                </span>
                <ChevronRight size={17} />
              </summary>
              <div>
                <Field label="What should the designer remember?">
                  <TextArea
                    rows={3}
                    maxLength={2000}
                    placeholder="Example: Keep the title very large and easy to read."
                    value={project.production.coverNotes}
                    onChange={(event) => updateProduction({ coverNotes: event.target.value })}
                  />
                </Field>
              </div>
            </details>
          </div>
          <WorkflowFinish
            label={isBook ? "Inside pages" : "Product pages"}
            onClick={() => setActiveStage("interior")}
            disabled={!coverReady}
          />
        </section>
      ) : null}

      {activeStage === "interior" ? (
        <section className="workflow-stage" role="tabpanel">
          <SectionHeading
            eyebrow={isBook ? "Inside pages" : "Product pages"}
            title={isBook ? "Inside pages" : "Product pages"}
          />
          {project.catalogue.length > 1 ? (
          <div className="expansion-toolbar">
            <Field label={`Working ${itemNoun}`}>
              <AppSelect
                value={activeBook?.id ?? ""}
                onChange={(event) => setActiveBookId(event.target.value)}
                aria-label="Interior plan title"
              >
                {project.catalogue.map((book) => (
                  <option key={book.id} value={book.id}>
                    {book.sequence}. {book.title || `Untitled ${itemNoun}`}
                  </option>
                ))}
              </AppSelect>
            </Field>
            <div className="expansion-toolbar__status">
              <Badge tone="green">
                {activeInteriorPlan.layoutIds.length} {activeInteriorPlan.layoutIds.length === 1 ? "layout" : "layouts"}
              </Badge>
              <Badge>
                {activeInteriorPlan.moduleIds.length} {activeInteriorPlan.moduleIds.length === 1 ? "page type" : "page types"}
              </Badge>
            </div>
          </div>
          ) : null}

          <div className="form-card book-page-plan-card">
            <div className="book-page-plan-heading">
              <div>
                <span className="eyebrow">Pages in this {itemNoun}</span>
                <h3>{activeBook?.title || `Untitled ${itemNoun}`}</h3>
              </div>
              <Badge tone={selectedPagePlan.length ? "green" : "neutral"}>
                {selectedPagePlan.length} {selectedPagePlan.length === 1 ? "page" : "pages"}
              </Badge>
            </div>
            <div className="book-page-plan-list">
              {selectedPagePlan.map((page) => (
                <article key={page.key}>
                  <span className="book-page-plan-list__icon"><LayoutTemplate size={17} /></span>
                  <div>
                    <strong>{page.name}</strong>
                    <p>{page.purpose}</p>
                  </div>
                  <Badge>{pageUseLabel(page.id, page.name)}</Badge>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label={`Remove ${page.name}`}
                    onClick={() =>
                      page.source === "layout"
                        ? updateInteriorPlan({
                            layoutIds: activeInteriorPlan.layoutIds.filter(
                              (id) => id !== page.id,
                            ),
                          })
                        : updateInteriorPlan({
                            moduleIds: activeInteriorPlan.moduleIds.filter(
                              (id) => id !== page.id,
                            ),
                          })
                    }
                  >
                    <Trash2 size={15} />
                  </button>
                </article>
              ))}
              {!selectedPagePlan.length ? (
                <p className="book-page-plan-list__empty">
                  No pages selected.
                </p>
              ) : null}
            </div>
            <Field label="Page order">
              <TextArea
                rows={4}
                maxLength={2000}
                placeholder="Example: Begin with instructions, repeat the main record page, use supporting pages when needed, then finish with a closing review."
                value={activeInteriorPlan.sequenceNotes}
                onChange={(event) => updateInteriorPlan({ sequenceNotes: event.target.value })}
              />
            </Field>
          </div>

          <details className="beginner-advanced page-plan-advanced">
            <summary>
              <span>
                <strong>Change the page plan (optional)</strong>
                <small>Add, replace or remove pages.</small>
              </span>
              <ChevronRight size={17} />
            </summary>
            <div className="page-plan-editor">
              <strong>Recommended starting pages</strong>
          <div className="layout-master-grid">
            {suggestedLayouts.map((layout) => {
              const selected = activeInteriorPlan.layoutIds.includes(layout.id);
              return (
                <button
                  key={layout.id}
                  type="button"
                  className={selected ? "is-selected" : ""}
                  onClick={() =>
                    updateInteriorPlan({
                      layoutIds: selected
                        ? activeInteriorPlan.layoutIds.filter((id) => id !== layout.id)
                        : [...activeInteriorPlan.layoutIds, layout.id],
                    })
                  }
                >
                  <span><LayoutTemplate size={17} /></span>
                  <strong>{layout.name}</strong>
                  <p>{layout.purpose}</p>
                  <small>{layout.density} · {layout.structure}</small>
                  {selected ? <Check size={16} /> : <Plus size={16} />}
                </button>
              );
            })}
          </div>

          <div className="form-card expansion-form-grid">
            <div>
              <strong>Selected page layouts</strong>
              <div className="selected-token-list">
                {activeInteriorPlan.layoutIds.map((id) => {
                  const layout = interiorLayoutMasters.find((item) => item.id === id);
                  return (
                    <button
                      type="button"
                      key={id}
                      aria-label={`Remove ${layout?.name ?? id}`}
                      onClick={() =>
                        updateInteriorPlan({
                          layoutIds: activeInteriorPlan.layoutIds.filter(
                            (item) => item !== id,
                          ),
                        })
                      }
                    >
                      {layout?.name ?? id} <span aria-hidden="true">×</span>
                    </button>
                  );
                })}
                {!activeInteriorPlan.layoutIds.length ? (
                  <small>No page layouts selected yet.</small>
                ) : null}
              </div>
            </div>
            <Field label="Add another starting page (optional)">
              <div className="inline-field">
                <AppSelect
                  value={layoutToAdd}
                  onChange={(event) => setLayoutToAdd(event.target.value)}
                  aria-label="Additional page layout"
                >
                  <option value="">Choose a page</option>
                  {interiorLayoutMasters
                    .filter((layout) => !activeInteriorPlan.layoutIds.includes(layout.id))
                    .map((layout) => (
                      <option key={layout.id} value={layout.id}>{layout.name} - {layout.purpose}</option>
                    ))}
                </AppSelect>
                <Button
                  size="sm"
                  disabled={!layoutToAdd}
                  onClick={() => {
                    updateInteriorPlan({
                      layoutIds: [...activeInteriorPlan.layoutIds, layoutToAdd],
                    });
                    setLayoutToAdd("");
                  }}
                >
                  <Plus size={15} /> Add
                </Button>
              </div>
            </Field>
            <Field
              label="Add a repeating or special page (optional)"
              hint="Use this only when the approved plan is missing something important."
            >
              <div className="inline-field">
                <AppSelect
                  value={moduleToAdd}
                  onChange={(event) => setModuleToAdd(event.target.value)}
                  aria-label="Interior module to add"
                >
                  <option value="">Choose a page</option>
                  {availableModules
                    .filter((module) => !activeInteriorPlan.moduleIds.includes(module.id))
                    .map((module) => (
                      <option key={module.id} value={module.id}>
                        {suggestedModuleIds.includes(module.id) ? "Recommended - " : ""}
                        {module.name}
                      </option>
                    ))}
                </AppSelect>
                <Button
                  size="sm"
                  disabled={!moduleToAdd}
                  onClick={() => {
                    updateInteriorPlan({
                      moduleIds: [...activeInteriorPlan.moduleIds, moduleToAdd],
                    });
                    setModuleToAdd("");
                  }}
                >
                  <Plus size={15} /> Add
                </Button>
              </div>
            </Field>
            <div className="selected-token-list">
              {activeInteriorPlan.moduleIds.map((id) => {
                const module = interiorModules.find((item) => item.id === id);
                return (
                  <button
                    type="button"
                    key={id}
                    onClick={() =>
                      updateInteriorPlan({
                        moduleIds: activeInteriorPlan.moduleIds.filter((item) => item !== id),
                      })
                    }
                  >
                    {module?.name ?? id} <span aria-hidden="true">×</span>
                  </button>
                );
              })}
              {!activeInteriorPlan.moduleIds.length ? <small>No modules selected yet.</small> : null}
            </div>
          </div>
            </div>
          </details>
          <WorkflowFinish
            label="Create files"
            onClick={() => setActiveStage("specs")}
            disabled={!interiorReady}
          />
        </section>
      ) : null}

      {activeStage === "specs" ? (
        <section className="workflow-stage" role="tabpanel">
          <SectionHeading
            eyebrow="Create files"
            title={marketplaceGuide.usesKdpPrintSetup ? "Create the PDFs" : `Create the ${primaryFileLabel.toLowerCase()}`}
            actions={<Badge tone="green">{marketplaceGuide.shortName}</Badge>}
          />
          {isBook ? (
            <div className="customer-file-flow">
              <article className="form-card customer-file-step">
                <div className="customer-file-step__heading">
                  <span>1</span>
                  <div>
                    <strong>Print setup</strong>
                  </div>
                </div>
                <div className="production-spec-grid">
                  <Field label="Finished book size">
                    <AppSelect
                      value={project.production.trimSize}
                      onChange={(event) => updateFileSetup({ trimSize: event.target.value })}
                      aria-label="Finished book size"
                    >
                      {["5 × 8 in", "5.5 × 8.5 in", "6 × 9 in", "7 × 10 in", "8 × 10 in", "8.5 × 11 in", "Custom / verify"].map((value) => (
                        <option key={value}>{value}</option>
                      ))}
                    </AppSelect>
                  </Field>
                  <Field label="Paper">
                    <AppSelect
                      value={project.production.paperType}
                      onChange={(event) => updateFileSetup({ paperType: event.target.value })}
                      aria-label="Paper type"
                    >
                      <option>White paper</option>
                      <option>Cream paper</option>
                      <option>Premium colour</option>
                      <option>Standard colour</option>
                    </AppSelect>
                  </Field>
                  <Field label="Do images reach the edge of the page?">
                    <AppSelect
                      value={project.production.bleed}
                      onChange={(event) =>
                        updateFileSetup({ bleed: event.target.value as "No bleed" | "Bleed" })
                      }
                      aria-label="Bleed setting"
                    >
                      <option value="No bleed">No - keep a white margin</option>
                      <option value="Bleed">Yes - artwork reaches the edge</option>
                    </AppSelect>
                  </Field>
                </div>
                <details className="beginner-advanced">
                  <summary>
                    <span><strong>Print notes (optional)</strong></span>
                    <ChevronRight size={17} />
                  </summary>
                  <div>
                    <Field label="Readability and font notes">
                      <TextArea
                        rows={3}
                        maxLength={1200}
                        placeholder="Example: Use licensed fonts, clear headings and enough room to write."
                        value={project.production.typographySpecification}
                        onChange={(event) =>
                          updateFileSetup({ typographySpecification: event.target.value })
                        }
                      />
                    </Field>
                  </div>
                </details>
              </article>

              <article className="form-card customer-file-step">
                <div className="customer-file-step__heading">
                  <span>2</span>
                  <div>
                    <strong>{primaryFileLabel}</strong>
                  </div>
                  {interiorFileReady ? <Badge tone="green">Created</Badge> : null}
                </div>
                <div className="customer-file-handoff">
                  <span className="eyebrow">Pages</span>
                  <div className="selected-token-list">
                    {selectedPagePlan.map((page) => {
                      const count = adaptivePageBlueprint?.entries.find(
                        (entry) => entry.name === page.name,
                      )?.count;
                      return <span key={page.key}>{page.name}{count ? ` × ${count}` : ""}</span>;
                    })}
                  </div>
                </div>
                <div className="file-creation-action">
                  <div className="file-creation-action__heading">
                    <FileCheck2 size={20} />
                    <div>
                      <strong>Create the {primaryFileLabel.toLowerCase()}</strong>
                      <small>{project.production.trimSize} · {adaptivePageBlueprint?.targetPageCount ?? project.production.plannedPageCount} pages</small>
                    </div>
                    <Button
                      size="sm"
                      onClick={() =>
                        copyFileCreationPrompt("primary", primaryCreationPrompt)
                      }
                    >
                      {filePromptCopyState === "primary" ? <Check size={15} /> : <Copy size={15} />}
                      {filePromptCopyState === "primary"
                        ? "Prompt copied"
                        : filePromptCopyState === "error"
                          ? "Copy failed"
                          : "Copy prompt"}
                    </Button>
                  </div>
                  <details>
                    <summary>View prompt</summary>
                    <pre>{primaryCreationPrompt}</pre>
                  </details>
                </div>
                <div className="check-list file-created-check">
                  <button
                    type="button"
                    className={interiorFileReady ? "is-complete" : ""}
                    onClick={togglePrimaryFileCreated}
                  >
                    <span>{interiorFileReady ? <Check size={15} /> : null}</span>
                    {primaryFileLabel} created
                  </button>
                </div>
              </article>

              {interiorFileReady ? (
                <article className="form-card customer-file-step">
                  <div className="customer-file-step__heading">
                    <span>3</span>
                    <div>
                      <strong>Cover preview</strong>
                    </div>
                    {coverFileReady ? <Badge tone="green">Preview ready</Badge> : null}
                  </div>
                  <div className="customer-file-summary">
                    <span><small>Size</small><strong>{project.production.trimSize}</strong></span>
                    <span><small>Paper</small><strong>{project.production.paperType}</strong></span>
                    <span><small>Pages</small><strong>{project.production.plannedPageCount}</strong></span>
                  </div>
                  <div className="customer-file-handoff">
                    <span className="eyebrow">Cover</span>
                    <strong>{activeBook?.title}</strong>
                    <p>{selectedSystem?.name} · {selectedPalette?.name}</p>
                    <span className="eyebrow">Typography</span>
                    <small>{selectedSystem ? `${selectedSystem.typeRule}. Sized automatically for this title and template.` : "Matched automatically to the selected cover style."}</small>
                    <span className="eyebrow">Back cover</span>
                    <small>{activeCoverPlan.backCoverCopy}</small>
                  </div>
                  <div className="external-resource">
                    <ExternalLink size={18} />
                    <div>
                      <strong>Download the exact cover template</strong>
                      <small>KDP uses the finished {project.production.plannedPageCount}-page interior to calculate the spine and full-cover size.</small>
                    </div>
                    <a
                      className="button button--secondary button--sm"
                      href="https://kdp.amazon.com/cover-calculator"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Get cover template
                    </a>
                  </div>
                  <div className="file-creation-action">
                    <div className="file-creation-action__heading">
                      <FileCheck2 size={20} />
                      <div>
                        <strong>Create the cover preview</strong>
                        <small>Attach both files, then paste the prompt. Use the result as the visual reference for the final Canva file.</small>
                      </div>
                      <Button
                        size="sm"
                        onClick={() =>
                          copyFileCreationPrompt("cover", coverCreationPrompt)
                        }
                      >
                        {filePromptCopyState === "cover" ? <Check size={15} /> : <Copy size={15} />}
                        {filePromptCopyState === "cover"
                          ? "Prompt copied"
                          : filePromptCopyState === "error"
                            ? "Copy failed"
                            : "Copy prompt"}
                      </Button>
                    </div>
                    <div className="selected-token-list" aria-label="Files to attach with the prompt">
                      <span>Finished cover artwork</span>
                      <span>Downloaded KDP cover template</span>
                    </div>
                    <details>
                      <summary>View prompt</summary>
                      <pre>{coverCreationPrompt}</pre>
                    </details>
                  </div>
                  <div className="check-list file-created-check">
                    <button
                      type="button"
                      className={coverFileReady ? "is-complete" : ""}
                      onClick={toggleCoverFileCreated}
                    >
                      <span>{coverFileReady ? <Check size={15} /> : null}</span>
                      Cover preview created
                    </button>
                  </div>
                  {coverFileReady ? (
                    <small className="form-help">
                      Build the final upload file in Canva or another design tool using this preview and the KDP template.
                    </small>
                  ) : null}
                </article>
              ) : null}

              {customerFilesReady ? (
                <WorkflowFinish
                  label="Prepare to sell"
                  onClick={onComplete}
                />
              ) : null}
            </div>
          ) : (
            <div className="customer-file-flow">
              <article className="form-card customer-file-step">
                <div className="customer-file-step__heading">
                  <span>1</span>
                  <div><strong>{primaryFileLabel}</strong></div>
                  {interiorFileReady ? <Badge tone="green">Created</Badge> : null}
                </div>
                <div className="customer-file-handoff">
                  <span className="eyebrow">Pages</span>
                  <div className="selected-token-list">
                    {selectedPagePlan.map((page) => {
                      const count = adaptivePageBlueprint?.entries.find(
                        (entry) => entry.name === page.name,
                      )?.count;
                      return <span key={page.key}>{page.name}{count ? ` × ${count}` : ""}</span>;
                    })}
                  </div>
                </div>
                <div className="file-creation-action">
                  <div className="file-creation-action__heading">
                    <FileCheck2 size={20} />
                    <div>
                      <strong>Create the {primaryFileLabel.toLowerCase()}</strong>
                      <small>{project.format} · {adaptivePageBlueprint?.targetPageCount ?? "Complete"} pages · {marketplaceGuide.shortName}</small>
                    </div>
                    <Button
                      size="sm"
                      onClick={() =>
                        copyFileCreationPrompt("primary", primaryCreationPrompt)
                      }
                    >
                      {filePromptCopyState === "primary" ? <Check size={15} /> : <Copy size={15} />}
                      {filePromptCopyState === "primary"
                        ? "Prompt copied"
                        : filePromptCopyState === "error"
                          ? "Copy failed"
                          : "Copy prompt"}
                    </Button>
                  </div>
                  <details>
                    <summary>View prompt</summary>
                    <pre>{primaryCreationPrompt}</pre>
                  </details>
                </div>
                <div className="check-list file-created-check">
                  <button
                    type="button"
                    className={interiorFileReady ? "is-complete" : ""}
                    onClick={togglePrimaryFileCreated}
                  >
                    <span>{interiorFileReady ? <Check size={15} /> : null}</span>
                    {primaryFileLabel} created
                  </button>
                </div>
              </article>
              {customerFilesReady ? (
                <WorkflowFinish
                  label="Prepare to sell"
                  onClick={onComplete}
                />
              ) : null}
            </div>
          )}
        </section>
      ) : null}

      {activeStage === "preflight" ? (
        <section className="workflow-stage" role="tabpanel">
          <SectionHeading
            eyebrow="Final checks"
            title="Do not mark a check until you have evidence"
            description="An empty check is useful. It shows exactly what still needs to happen before selling."
          />
          <div className="preflight-grid">
            <article className="expansion-detail-card">
              <h3>Required before selling</h3>
              <div className="check-list">
                {preflightChecks.map((check) => {
                  const done = Boolean(project.production.preflight[check]);
                  return (
                    <button
                      type="button"
                      className={done ? "is-complete" : ""}
                      key={check}
                      onClick={() =>
                        updateProduction({
                          preflight: { ...project.production.preflight, [check]: !done },
                        })
                      }
                    >
                      <span>{done ? <Check size={15} /> : null}</span>
                      {check}
                    </button>
                  );
                })}
              </div>
            </article>

            <article className="expansion-detail-card">
              <h3>Where did the content and artwork come from?</h3>
              <div className="compact-form-grid">
                <TextInput
                  aria-label="Rights asset"
                  placeholder="Example: Main cover illustration"
                  value={rightsDraft.asset}
                  onChange={(event) => setRightsDraft({ ...rightsDraft, asset: event.target.value })}
                />
                <TextInput
                  aria-label="Rights source"
                  placeholder="Example: Saved source file or receipt location"
                  value={rightsDraft.source}
                  onChange={(event) => setRightsDraft({ ...rightsDraft, source: event.target.value })}
                />
                <TextInput
                  aria-label="Rights licence"
                  placeholder="Example: Commercial use - still needs checking"
                  value={rightsDraft.licence}
                  onChange={(event) => setRightsDraft({ ...rightsDraft, licence: event.target.value })}
                />
                <AppSelect
                  value={rightsDraft.status}
                  onChange={(event) =>
                    setRightsDraft({
                      ...rightsDraft,
                      status: event.target.value as typeof rightsDraft.status,
                    })
                  }
                  aria-label="Rights status"
                >
                  <option value="to-check">To check</option>
                  <option value="cleared">Cleared</option>
                  <option value="restricted">Restricted</option>
                </AppSelect>
                <Button size="sm" disabled={!rightsDraft.asset.trim()} onClick={addRightsItem}>
                  <Plus size={15} /> Add record
                </Button>
              </div>
              <div className="record-list">
                {project.production.rightsLedger.map((item) => (
                  <div key={item.id}>
                    <span className={`record-status record-status--${item.status}`} />
                    <div>
                      <strong>{item.asset}</strong>
                      <small>{item.source || "Source not recorded"} · {item.licence || "Licence not recorded"}</small>
                    </div>
                    <button
                      className="icon-button"
                      type="button"
                      aria-label={`Remove ${item.asset}`}
                      onClick={() =>
                        updateProduction({
                          rightsLedger: project.production.rightsLedger.filter(
                            (candidate) => candidate.id !== item.id,
                          ),
                        })
                      }
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
                {!project.production.rightsLedger.length ? <small>No rights records yet.</small> : null}
              </div>
            </article>

            <article className="expansion-detail-card">
              <h3>{marketplaceGuide.usesKdpPrintSetup ? "Printed proof review" : "Test purchase or file review"}</h3>
              <div className="compact-form-grid">
                <AppSelect
                  value={proofDraft.bookId}
                  onChange={(event) => setProofDraft({ ...proofDraft, bookId: event.target.value })}
                  aria-label="Reviewed product"
                >
                  {project.catalogue.map((book) => (
                    <option value={book.id} key={book.id}>{book.title || `Title ${book.sequence}`}</option>
                  ))}
                </AppSelect>
                <TextInput
                  type="date"
                  aria-label="Review date"
                  value={proofDraft.reviewedAt}
                  onChange={(event) =>
                    setProofDraft({ ...proofDraft, reviewedAt: event.target.value })
                  }
                />
                <AppSelect
                  value={proofDraft.outcome}
                  onChange={(event) =>
                    setProofDraft({
                      ...proofDraft,
                      outcome: event.target.value as typeof proofDraft.outcome,
                    })
                  }
                  aria-label="Review outcome"
                >
                  <option value="pending">Pending</option>
                  <option value="changes-needed">Changes needed</option>
                  <option value="approved">Approved</option>
                </AppSelect>
                <TextInput
                  aria-label="Review notes"
                  placeholder="What worked, what must change, and what you will test again"
                  value={proofDraft.notes}
                  onChange={(event) => setProofDraft({ ...proofDraft, notes: event.target.value })}
                />
                <Button
                  size="sm"
                  disabled={proofMissing.length > 0}
                  onClick={addProof}
                >
                  <Plus size={15} /> Save review
                </Button>
              </div>
              {proofMissing.length ? (
                <small className="form-help form-help--warning" role="status">
                  To save this review: {proofMissing.join(", ")}.
                </small>
              ) : (
                <small className="form-help form-help--success">
                  Everything needed to save this review is ready.
                </small>
              )}
              <div className="record-list">
                {project.production.proofLog.map((item) => {
                  const book = project.catalogue.find((candidate) => candidate.id === item.bookId);
                  return (
                    <div key={item.id}>
                      <span className={`record-status record-status--${item.outcome}`} />
                      <div>
                        <strong>{book?.title || `Untitled ${itemNoun}`}</strong>
                        <small>{item.reviewedAt || "Date not recorded"} · {item.outcome.replace("-", " ")}</small>
                      </div>
                      <button
                        className="icon-button"
                        type="button"
                        aria-label={`Remove proof record for ${book?.title || "title"}`}
                        onClick={() =>
                          updateProduction({
                            proofLog: project.production.proofLog.filter(
                              (candidate) => candidate.id !== item.id,
                            ),
                          })
                        }
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  );
                })}
                {!project.production.proofLog.length ? <small>No proof reviews yet.</small> : null}
              </div>
            </article>
          </div>

          <div className="form-card">
            <Field label="What still needs to happen?">
              <TextArea
                rows={5}
                maxLength={3000}
                placeholder="Example: Finish the inside PDF, calculate the final cover size, check licences, test the customer file, then review the listing."
                value={project.production.revisionNotes}
                onChange={(event) => updateProduction({ revisionNotes: event.target.value })}
              />
            </Field>
            <div className="expansion-export-row">
              <div>
                <strong>Production notes</strong>
                <small>A portable record of the {visualNoun}, pages, customer files and checks.</small>
              </div>
              <Button
                onClick={() => {
                  downloadProductionDossier(project);
                  setDownloaded(true);
                  window.setTimeout(() => setDownloaded(false), 4_000);
                }}
              >
                {downloaded ? <Check size={16} /> : <Download size={16} />}
                {downloaded ? "Download started" : "Download production notes"}
              </Button>
            </div>
          </div>
          <WorkflowFinish label="Prepare to sell" onClick={onComplete} />
        </section>
      ) : null}
    </div>
  );
}

const launchStages: Stage[] = [
  { id: "listing", label: "Selling kit", icon: SearchCheck },
  { id: "launch", label: "Publishing checklist", icon: ListChecks },
];
const optionalLaunchStage: Stage = {
  id: "extras",
  label: "Optional tools",
  icon: Sparkles,
};

function createListing(): ListingRecord {
  return ListingRecordSchema.parse({});
}

export function LaunchGrowthPanel({
  project,
  entitlements,
  setProject,
  onComplete,
}: {
  project: PaperKingdomProject;
  entitlements: OfferSku[];
  setProject: SetProject;
  onComplete: () => void;
}) {
  const [activeStage, setActiveStage] = useState("listing");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const [kitAnswer, setKitAnswer] = useState("");
  const [kitImportState, setKitImportState] = useState<"idle" | "success" | "error">("idle");
  const [kitImportMessage, setKitImportMessage] = useState("");
  const [manualKitOpen, setManualKitOpen] = useState(false);
  const [sellingKitDownloaded, setSellingKitDownloaded] = useState(false);
  const hasLaunchGrowth = entitlements.includes("paper-kingdom-launch-growth");

  useEffect(() => {
    setKitAnswer("");
    setKitImportState("idle");
    setKitImportMessage("");
    setManualKitOpen(false);
  }, [project.marketplace, project.launchGrowth.activeBookId]);

  if (!hasLaunchGrowth) {
    return (
      <LockedExpansion
        offer="paper-kingdom-launch-growth"
        title="Selling tools are not enabled"
        description="Unlock listing records, A+ storyboards, launch planning, reader funnels, cross-sells, and a consolidated growth package."
      />
    );
  }

  const marketplaceGuide = getMarketplaceGuide(project.marketplace);
  const sellingGuide = getSellingKitGuide(project.marketplace);
  const itemNoun = marketplaceGuide.usesKdpPrintSetup ? "book" : "product";
  const activeBookId = project.launchGrowth.activeBookId ?? project.catalogue[0]?.id ?? "";
  const activeBook =
    project.catalogue.find((book) => book.id === activeBookId) ?? project.catalogue[0];
  const listing =
    (activeBook && getChannelListing(project, activeBook.id)) ?? createListing();
  const leadMagnet =
    leadMagnetSystems.find((system) => system.id === project.launchGrowth.leadMagnetId) ??
    leadMagnetSystems[0];
  const welcome =
    welcomeSequences.find(
      (sequence) => sequence.id === project.launchGrowth.welcomeSequenceId,
    ) ?? welcomeSequences[0];
  const accountReady = getChannelAccountReady(project);
  const updateGrowth = (patch: Partial<PaperKingdomProject["launchGrowth"]>) =>
    setProject((current) => ({
      ...current,
      launchGrowth: { ...current.launchGrowth, ...patch },
    }));
  const updateListing = (patch: Partial<ListingRecord>) => {
    if (!activeBook) return;
    const currentChannelListings =
      project.launchGrowth.listingsByMarketplace[project.marketplace] ?? {};
    updateGrowth({
      listingsByMarketplace: {
        ...project.launchGrowth.listingsByMarketplace,
        [project.marketplace]: {
          ...currentChannelListings,
          [activeBook.id]: { ...listing, ...patch },
        },
      },
    });
  };

  const updateModule = (id: string, patch: Partial<APlusModule>) =>
    updateGrowth({
      aPlusModules: project.launchGrowth.aPlusModules.map((module) =>
        module.id === id ? { ...module, ...patch } : module,
      ),
    });

  const moveModule = (id: string, direction: -1 | 1) => {
    const currentIndex = project.launchGrowth.aPlusModules.findIndex((module) => module.id === id);
    const targetIndex = currentIndex + direction;
    if (
      currentIndex < 0 ||
      targetIndex < 0 ||
      targetIndex >= project.launchGrowth.aPlusModules.length
    ) return;
    const modules = [...project.launchGrowth.aPlusModules];
    [modules[currentIndex], modules[targetIndex]] = [modules[targetIndex], modules[currentIndex]];
    updateGrowth({ aPlusModules: modules });
  };

  const listingReady = Boolean(
    activeBook?.title.trim() &&
      (!sellingGuide.titleEditable || listing.listingTitle.trim()) &&
      listing.description.trim() &&
      listing.audienceDetails.trim() &&
      listing.keywords
        .slice(0, sellingGuide.searchCount)
        .filter((keyword) => keyword.trim()).length === sellingGuide.searchCount,
  );
  const promotionReady = Boolean(
    listing.shortPitch.trim() &&
      listing.socialPosts.filter((post) => post.trim()).length === 3 &&
      listing.launchEmailSubject.trim() &&
      listing.launchEmailBody.trim(),
  );
  const visualPlanReady =
    listing.visualPlan
      .slice(0, sellingGuide.visualCount)
      .filter((item) => item.trim()).length === sellingGuide.visualCount;
  const sellingKitReady = listingReady && promotionReady && visualPlanReady;
  const sellingKitHasContent = Boolean(
    listing.description.trim() ||
      listing.shortPitch.trim() ||
      listing.socialPosts.some((post) => post.trim()) ||
      listing.visualPlan.some((item) => item.trim()),
  );
  const primaryFileReady = Boolean(
    activeBook &&
      project.production.primaryFileCreated &&
      project.production.primaryFilePromptSignature ===
        getPrimaryFileBlueprintSignature(project, activeBook.id),
  );
  const coverFileReady = Boolean(
    !marketplaceGuide.usesKdpPrintSetup ||
      (activeBook &&
        project.production.coverPdfCreated &&
        project.production.coverFilePromptSignature ===
          getCoverFileBlueprintSignature(project, activeBook.id)),
  );
  const customerFilesReady = primaryFileReady && coverFileReady;

  const copyListingPrompt = async () => {
    try {
      await copyText(
        composeSellingKitPrompt(project, activeBook?.id ?? ""),
      );
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
    window.setTimeout(() => setCopyState("idle"), 1800);
  };

  const applySellingKitAnswer = () => {
    const result = parseSellingKitAnswer(kitAnswer, sellingGuide);
    if (!result.ok) {
      setKitImportState("error");
      setKitImportMessage(result.message);
      return;
    }
    updateListing({
      listingTitle: sellingGuide.titleEditable
        ? result.data.listingTitle || activeBook?.title || ""
        : listing.listingTitle,
      description: result.data.description,
      audienceDetails: result.data.idealBuyer,
      keywords: result.data.searchTerms,
      seoTitle: sellingGuide.showSeoFields ? result.data.seoTitle : "",
      seoDescription: sellingGuide.showSeoFields ? result.data.seoDescription : "",
      shortPitch: result.data.shortPitch,
      socialPosts: result.data.socialPosts,
      launchEmailSubject: result.data.launchEmailSubject,
      launchEmailBody: result.data.launchEmailBody,
      visualPlan: result.data.visualPlan,
    });
    setKitAnswer("");
    setKitImportState("success");
    setKitImportMessage("Selling kit added. Review the copy below before using it.");
    setManualKitOpen(true);
  };

  const downloadSellingKit = () => {
    downloadGrowthPackage(project);
    setSellingKitDownloaded(true);
    window.setTimeout(() => setSellingKitDownloaded(false), 1800);
  };

  return (
    <div className="studio-panel expansion-panel">
      <ExpansionNavigator
        stages={activeStage === "extras" ? [...launchStages, optionalLaunchStage] : launchStages}
        activeStage={activeStage}
        onChange={setActiveStage}
      />

      {activeStage === "listing" ? (
        <section className="workflow-stage" role="tabpanel">
          <SectionHeading
            eyebrow="Selling kit"
            title={`Create the ${marketplaceGuide.listingName} and promotion copy`}
            description={`Paper Kingdom uses the ${itemNoun} plan, finished files, colours, audience, and ${marketplaceGuide.shortName} requirements to create one practical selling kit.`}
          />
          {project.catalogue.length > 1 ? (
            <div className="expansion-toolbar expansion-toolbar--single">
              <Field label={`Which ${itemNoun} are you preparing to sell?`}>
                <AppSelect
                  value={activeBook?.id ?? ""}
                  onChange={(event) => updateGrowth({ activeBookId: event.target.value })}
                  aria-label="Listing title"
                >
                  {project.catalogue.map((book) => (
                    <option key={book.id} value={book.id}>
                      {book.sequence}. {book.title || `Untitled ${itemNoun}`}
                    </option>
                  ))}
                </AppSelect>
              </Field>
            </div>
          ) : null}
          <div className="selling-kit-builder">
            <div>
              <Badge tone="green">Made for {marketplaceGuide.shortName}</Badge>
              <h3>Create the complete selling kit in one go</h3>
              <p>Copy one prompt. Use it in your AI tool. Bring the answer back and Paper Kingdom fills the useful parts below.</p>
            </div>
            <div className="selling-kit-outcomes">
              <span><SearchCheck size={18} /><strong>Product page</strong><small>Title, description and {sellingGuide.searchCount} search terms</small></span>
              <span><Megaphone size={18} /><strong>Promotion copy</strong><small>Pitch, three posts and one email</small></span>
              <span><Image size={18} /><strong>{sellingGuide.visualLabel}</strong><small>What each useful image should show</small></span>
            </div>
            <div className="selling-kit-actions">
              <Button onClick={copyListingPrompt}>
                {copyState === "copied" ? <Check size={16} /> : <Copy size={16} />}
                {copyState === "copied" ? "Selling-kit prompt copied" : copyState === "error" ? "Copy failed" : "Copy selling-kit prompt"}
              </Button>
              <span>1. Copy prompt&nbsp;&nbsp; 2. Use it in your AI tool&nbsp;&nbsp; 3. Paste the answer below</span>
            </div>
            <Field label="Paste the completed selling-kit answer">
              <TextArea
                rows={7}
                maxLength={50000}
                placeholder="Paste the complete answer from your AI tool here."
                value={kitAnswer}
                onChange={(event) => {
                  setKitAnswer(event.target.value);
                  setKitImportState("idle");
                  setKitImportMessage("");
                }}
              />
            </Field>
            <div className="selling-kit-import-row">
              <Button disabled={!kitAnswer.trim()} onClick={applySellingKitAnswer}>
                <Import size={16} /> Fill my selling kit
              </Button>
              {!sellingKitHasContent ? (
                <Button variant="ghost" onClick={() => setManualKitOpen(true)}>
                  Enter it myself instead
                </Button>
              ) : null}
              {kitImportMessage ? (
                <span className={`selling-kit-message is-${kitImportState}`} role="status">
                  {kitImportState === "success" ? <Check size={15} /> : null}
                  {kitImportMessage}
                </span>
              ) : null}
            </div>
          </div>

          {sellingKitHasContent || manualKitOpen ? (
            <div className="selling-kit-results">
              <section className="form-card selling-kit-result-card">
                <header>
                  <span><SearchCheck size={19} /></span>
                  <div>
                    <strong>Product-page copy</strong>
                    <small>Review this, then paste it into the matching fields on {marketplaceGuide.shortName}.</small>
                  </div>
                </header>
                {sellingGuide.titleEditable ? (
                  <Field label="Listing title">
                    <TextInput
                      maxLength={240}
                      placeholder={activeBook?.title || `Name this ${itemNoun}`}
                      value={listing.listingTitle}
                      onChange={(event) => updateListing({ listingTitle: event.target.value })}
                    />
                  </Field>
                ) : (
                  <div className="listing-title-sync">
                    <Check size={16} />
                    <div>
                      <strong>{activeBook?.title || `Untitled ${itemNoun}`}</strong>
                      <p>KDP uses the exact title from the book and cover.</p>
                    </div>
                  </div>
                )}
                <Field label="Product description" hint={sellingGuide.descriptionFocus}>
                  <TextArea
                    rows={8}
                    maxLength={5000}
                    value={listing.description}
                    onChange={(event) => updateListing({ description: event.target.value })}
                  />
                </Field>
                <Field label="Ideal buyer" hint="This keeps the listing and promotion focused on the right person.">
                  <TextArea
                    rows={3}
                    maxLength={1200}
                    value={listing.audienceDetails}
                    onChange={(event) => updateListing({ audienceDetails: event.target.value })}
                  />
                </Field>
                <div className="selling-kit-list">
                  <div>
                    <strong>{sellingGuide.searchCount} {sellingGuide.searchLabel}</strong>
                    <small>{sellingGuide.searchHint}</small>
                  </div>
                  <div className="selling-search-grid">
                    {Array.from({ length: sellingGuide.searchCount }, (_, index) => listing.keywords[index] ?? "").map((keyword, index) => (
                      <TextInput
                        key={index}
                        aria-label={`${sellingGuide.searchLabel} ${index + 1}`}
                        maxLength={80}
                        placeholder={`${index + 1}`}
                        value={keyword}
                        onChange={(event) => {
                          const keywords = Array.from(
                            { length: Math.max(listing.keywords.length, sellingGuide.searchCount) },
                            (_, keywordIndex) => listing.keywords[keywordIndex] ?? "",
                          );
                          keywords[index] = event.target.value;
                          updateListing({ keywords });
                        }}
                      />
                    ))}
                  </div>
                </div>
                {sellingGuide.showSeoFields ? (
                  <div className="selling-seo-grid">
                    <Field label="Search-engine page title">
                      <TextInput
                        maxLength={240}
                        value={listing.seoTitle}
                        onChange={(event) => updateListing({ seoTitle: event.target.value })}
                      />
                    </Field>
                    <Field label="Search-engine description">
                      <TextArea
                        rows={3}
                        maxLength={500}
                        value={listing.seoDescription}
                        onChange={(event) => updateListing({ seoDescription: event.target.value })}
                      />
                    </Field>
                  </div>
                ) : null}
              </section>

              <section className="form-card selling-kit-result-card">
                <header>
                  <span><Megaphone size={19} /></span>
                  <div>
                    <strong>Promotion copy</strong>
                    <small>Useful starting copy for announcing the real finished product—without invented results.</small>
                  </div>
                </header>
                <Field label="Short pitch">
                  <TextArea
                    rows={3}
                    maxLength={800}
                    value={listing.shortPitch}
                    onChange={(event) => updateListing({ shortPitch: event.target.value })}
                  />
                </Field>
                <div className="selling-social-grid">
                  {Array.from({ length: 3 }, (_, index) => listing.socialPosts[index] ?? "").map((post, index) => (
                    <Field label={`Social post ${index + 1}`} key={index}>
                      <TextArea
                        rows={4}
                        maxLength={1600}
                        value={post}
                        onChange={(event) => {
                          const socialPosts = Array.from(
                            { length: 3 },
                            (_, postIndex) => listing.socialPosts[postIndex] ?? "",
                          );
                          socialPosts[index] = event.target.value;
                          updateListing({ socialPosts });
                        }}
                      />
                    </Field>
                  ))}
                </div>
                <div className="selling-email-grid">
                  <Field label="Launch email subject">
                    <TextInput
                      maxLength={240}
                      value={listing.launchEmailSubject}
                      onChange={(event) => updateListing({ launchEmailSubject: event.target.value })}
                    />
                  </Field>
                  <Field label="Launch email">
                    <TextArea
                      rows={7}
                      maxLength={4000}
                      value={listing.launchEmailBody}
                      onChange={(event) => updateListing({ launchEmailBody: event.target.value })}
                    />
                  </Field>
                </div>
              </section>

              <section className="form-card selling-kit-result-card">
                <header>
                  <span><Image size={19} /></span>
                  <div>
                    <strong>{sellingGuide.visualLabel}</strong>
                    <small>{sellingGuide.visualHint}</small>
                  </div>
                </header>
                <div className="selling-visual-grid">
                  {Array.from({ length: sellingGuide.visualCount }, (_, index) => listing.visualPlan[index] ?? "").map((item, index) => (
                    <Field label={`Image ${index + 1}`} key={index}>
                      <TextArea
                        rows={3}
                        maxLength={1200}
                        value={item}
                        onChange={(event) => {
                          const visualPlan = Array.from(
                            { length: sellingGuide.visualCount },
                            (_, planIndex) => listing.visualPlan[planIndex] ?? "",
                          );
                          visualPlan[index] = event.target.value;
                          updateListing({ visualPlan });
                        }}
                      />
                    </Field>
                  ))}
                </div>
              </section>

              <div className="selling-kit-download">
                <div>
                  <strong>Your selling kit stays with this project</strong>
                  <small>Download the listing copy, promotion copy, image plan, and publishing steps in one file.</small>
                </div>
                <Button variant="secondary" onClick={downloadSellingKit}>
                  {sellingKitDownloaded ? <Check size={16} /> : <Download size={16} />}
                  {sellingKitDownloaded ? "Download started" : "Download selling kit"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="selling-kit-empty">
              <div>
                <strong>No empty worksheets to complete first.</strong>
                <p>Use the selling-kit prompt above and the finished copy will appear here after you bring the answer back.</p>
              </div>
            </div>
          )}
          <WorkflowFinish
            label="Publishing checklist"
            onClick={() => setActiveStage("launch")}
          />
        </section>
      ) : null}

      {activeStage === "extras" && marketplaceGuide.supportsAPlus ? (
        <section className="workflow-stage" role="tabpanel">
          <SectionHeading
            eyebrow="Optional Amazon extra"
            title="Add extra product-page sections only if they help"
            description="This is not required for the first book. Use real final pages and never invent features."
            actions={
              <Button
                size="sm"
                disabled={project.launchGrowth.aPlusModules.length >= 5}
                onClick={() =>
                  updateGrowth({
                    aPlusModules: [
                      ...project.launchGrowth.aPlusModules,
                      {
                        id: crypto.randomUUID(),
                        storyboardId: aPlusStoryboards[0].id,
                        headline: "",
                        body: "",
                        imageBrief: "",
                      },
                    ],
                  })
                }
              >
                <Plus size={15} /> Add module
              </Button>
            }
          />
          <div className="a-plus-limit">
            <span style={{ width: `${(project.launchGrowth.aPlusModules.length / 5) * 100}%` }} />
            <small>{project.launchGrowth.aPlusModules.length} of 5 modules planned</small>
          </div>
          <div className="a-plus-module-list">
            {project.launchGrowth.aPlusModules.map((module, index) => {
              const storyboard =
                aPlusStoryboards.find((item) => item.id === module.storyboardId) ??
                aPlusStoryboards[0];
              return (
                <article key={module.id}>
                  <header>
                    <span>{index + 1}</span>
                    <div>
                      <strong>{storyboard.name}</strong>
                      <small>{storyboard.module} · {storyboard.job}</small>
                    </div>
                    <div>
                      <button
                        className="icon-button"
                        type="button"
                        aria-label={`Move module ${index + 1} up`}
                        disabled={index === 0}
                        onClick={() => moveModule(module.id, -1)}
                      >
                        <ArrowUp size={15} />
                      </button>
                      <button
                        className="icon-button"
                        type="button"
                        aria-label={`Move module ${index + 1} down`}
                        disabled={index === project.launchGrowth.aPlusModules.length - 1}
                        onClick={() => moveModule(module.id, 1)}
                      >
                        <ArrowDown size={15} />
                      </button>
                      <button
                        className="icon-button"
                        type="button"
                        aria-label={`Remove module ${index + 1}`}
                        onClick={() =>
                          updateGrowth({
                            aPlusModules: project.launchGrowth.aPlusModules.filter(
                              (candidate) => candidate.id !== module.id,
                            ),
                          })
                        }
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </header>
                  <div className="expansion-form-grid">
                    <Field label="Storyboard">
                      <AppSelect
                        value={module.storyboardId}
                        onChange={(event) =>
                          updateModule(module.id, { storyboardId: event.target.value })
                        }
                        aria-label={`Module ${index + 1} storyboard`}
                      >
                        {aPlusStoryboards.map((item) => (
                          <option value={item.id} key={item.id}>{item.name}</option>
                        ))}
                      </AppSelect>
                    </Field>
                    <Field label="Headline">
                      <TextInput
                        maxLength={240}
                        value={module.headline}
                        onChange={(event) =>
                          updateModule(module.id, { headline: event.target.value })
                        }
                      />
                    </Field>
                    <Field label="Body copy">
                      <TextArea
                        rows={4}
                        maxLength={2000}
                        value={module.body}
                        onChange={(event) =>
                          updateModule(module.id, { body: event.target.value })
                        }
                      />
                    </Field>
                    <Field label="Image brief">
                      <TextArea
                        rows={4}
                        maxLength={1200}
                        placeholder={storyboard.imageDirection}
                        value={module.imageBrief}
                        onChange={(event) =>
                          updateModule(module.id, { imageBrief: event.target.value })
                        }
                      />
                    </Field>
                  </div>
                </article>
              );
            })}
            {!project.launchGrowth.aPlusModules.length ? (
              <EmptyState
                icon={<Image size={22} />}
                title="Build a concise A+ storyboard"
                description="Add only the modules that materially help a reader understand fit, substance, or catalogue context."
              >
                <Button
                  onClick={() =>
                    updateGrowth({
                      aPlusModules: [{
                        id: crypto.randomUUID(),
                        storyboardId: aPlusStoryboards[0].id,
                        headline: "",
                        body: "",
                        imageBrief: "",
                      }],
                    })
                  }
                >
                  <Plus size={16} /> Add first module
                </Button>
              </EmptyState>
            ) : null}
          </div>
        </section>
      ) : null}

      {activeStage === "launch" ? (
        <section className="workflow-stage" role="tabpanel">
          <SectionHeading
            eyebrow="Publishing checklist"
            title="Check what is ready, then publish"
            description={marketplaceGuide.usesKdpPrintSetup
              ? "Use the files from Make the book. Preview the inside PDF and final cover in KDP, correct any problem, then publish."
              : `Use the customer file and ${marketplaceGuide.listingName} you already prepared, test them, then publish.`}
          />
          <div className="form-card publishing-status-card">
            <div>
              <strong>Work already checked</strong>
              <small>The app reads the work you completed earlier. You do not need to enter it again.</small>
            </div>
            <div className="publishing-readiness">
              <span className={customerFilesReady ? "is-complete" : ""}>
                {customerFilesReady ? <Check size={15} /> : <FileText size={15} />}
                {marketplaceGuide.usesKdpPrintSetup ? "Inside and cover files created" : "Customer file created"}
              </span>
              <span className={sellingKitReady ? "is-complete" : ""}>
                {sellingKitReady ? <Check size={15} /> : <SearchCheck size={15} />}
                Selling kit created
              </span>
            </div>
          </div>
          <div className="form-card publishing-final-fields">
            <div className="switch-field">
              <div>
                <strong>{marketplaceGuide.accountStepLabel}</strong>
                <small>{marketplaceGuide.accountStepHint}</small>
              </div>
              <button
                className={`switch ${accountReady ? "is-on" : ""}`}
                type="button"
                role="switch"
                aria-checked={accountReady}
                aria-label={marketplaceGuide.accountStepLabel}
                onClick={() =>
                  updateGrowth({
                    channelAccountReady: {
                      ...project.launchGrowth.channelAccountReady,
                      [project.marketplace]: !accountReady,
                    },
                  })
                }
              >
                <span />
              </button>
            </div>
          </div>
          <div className="form-card publishing-next-steps">
            <strong>Publish on {marketplaceGuide.shortName}</strong>
            <div className="simple-sequence">
              {sellingGuide.uploadSteps.map((item, index) => (
                <span key={item}><b>{index + 1}</b>{item}</span>
              ))}
            </div>
          </div>
          <WorkflowFinish label="Download or back up" onClick={onComplete} />
          <div className="optional-tools-entry">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setActiveStage("extras");
                returnToPanelTop();
              }}
            >
              Open optional selling tools
            </Button>
            <small>Only for A+ sections, a free resource, email follow-up, or related-product ideas. None are required to publish.</small>
          </div>
        </section>
      ) : null}

      {activeStage === "extras" ? (
        <section className="workflow-stage" role="tabpanel">
          <SectionHeading
            eyebrow="Optional"
            title={marketplaceGuide.optionalMarketingName}
            description="You can publish without a QR code, free download, email sequence, advertising campaign, or related-product path."
          />
          {!project.launchGrowth.funnelEnabled ? (
            <div className="beginner-callout beginner-callout--action">
              <div>
                <strong>Leave this off for a simpler first project.</strong>
                <p>Enable it only when you have a real resource, stable link, privacy information, consent, delivery and unsubscribe process.</p>
              </div>
              <Button
                variant="secondary"
                onClick={() => updateGrowth({ funnelEnabled: true })}
              >
                Enable optional follow-up
              </Button>
            </div>
          ) : (
          <>
          <div className="optional-section-toolbar">
            <div>
              <strong>Optional follow-up is enabled</strong>
              <small>Turn it off to remove this work from summaries and exports.</small>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => updateGrowth({ funnelEnabled: false })}
            >
              Turn off
            </Button>
          </div>
          <div className="funnel-choice-grid">
            {leadMagnetSystems.map((system) => (
              <button
                type="button"
                className={system.id === leadMagnet.id ? "is-selected" : ""}
                key={system.id}
                onClick={() => updateGrowth({ leadMagnetId: system.id })}
              >
                <span><KeyRound size={17} /></span>
                <strong>{system.name}</strong>
                <p>{system.promise}</p>
                <small>{system.structure}</small>
                {system.id === leadMagnet.id ? <Check size={16} /> : null}
              </button>
            ))}
          </div>
          <div className="form-card expansion-form-grid">
            <Field label="Link for the optional resource">
              <TextInput
                type="url"
                maxLength={600}
                placeholder="https://"
                value={project.launchGrowth.qrDestination}
                onChange={(event) => updateGrowth({ qrDestination: event.target.value })}
              />
            </Field>
            <Field label="How will you explain the optional resource?">
              <TextArea
                rows={4}
                maxLength={1200}
                placeholder="Explain exactly what they receive, where the link goes, and that joining is optional."
                value={project.launchGrowth.insertCopy}
                onChange={(event) => updateGrowth({ insertCopy: event.target.value })}
              />
            </Field>
            <Field label="Signup-page headline">
              <TextInput
                maxLength={240}
                value={project.launchGrowth.optInHeadline}
                onChange={(event) => updateGrowth({ optInHeadline: event.target.value })}
              />
            </Field>
            <Field label="Signup-page explanation">
              <TextArea
                rows={4}
                maxLength={1600}
                value={project.launchGrowth.optInBody}
                onChange={(event) => updateGrowth({ optInBody: event.target.value })}
              />
            </Field>
            <Field label="Optional welcome emails">
              <AppSelect
                value={welcome.id}
                onChange={(event) => updateGrowth({ welcomeSequenceId: event.target.value })}
                aria-label="Welcome sequence"
              >
                {welcomeSequences.map((sequence) => (
                  <option value={sequence.id} key={sequence.id}>{sequence.name}</option>
                ))}
              </AppSelect>
              <div className="email-sequence-preview">
                {welcome.emails.map((email, index) => (
                  <span key={email}><b>{index + 1}</b>{email}</span>
                ))}
              </div>
            </Field>
            <Field label="Consent and delivery notes">
              <TextArea
                rows={4}
                maxLength={2400}
                placeholder="Record consent wording, delivery test, privacy link, unsubscribe process, and who handles support."
                value={project.launchGrowth.funnelNotes}
                onChange={(event) => updateGrowth({ funnelNotes: event.target.value })}
              />
            </Field>
          </div>
          <div className="cross-sell-grid">
            {crossSellTemplates.map((template) => {
              const selected = project.launchGrowth.crossSellTemplateIds.includes(template.id);
              return (
                <button
                  type="button"
                  className={selected ? "is-selected" : ""}
                  key={template.id}
                  onClick={() =>
                    updateGrowth({
                      crossSellTemplateIds: selected
                        ? project.launchGrowth.crossSellTemplateIds.filter((id) => id !== template.id)
                        : [...project.launchGrowth.crossSellTemplateIds, template.id],
                    })
                  }
                >
                  <span><Link2 size={16} /></span>
                  <strong>{template.name}</strong>
                  <p>{template.trigger}</p>
                  <small>{template.purpose}</small>
                  {selected ? <Check size={15} /> : <Plus size={15} />}
                </button>
              );
            })}
          </div>
          </>
          )}
          <WorkflowFinish
            label="Publishing checklist"
            onClick={() => setActiveStage("launch")}
          />
        </section>
      ) : null}
    </div>
  );
}
